import { PencilLight, XIcon } from '@commenda/icons';
import { Input, InputErrorMessage } from '@commenda/kakashi';
import { TEquityInfoKS } from '@commenda/types/src/incorporation/types';
import { TSetNotification } from '@commenda/types/src/random';
import { getAvatarName } from '@commenda/utils';
import {
  ValidationRule,
  validateRules,
} from '@commenda/utils/equity-input-rules';
import React from 'react';
import ChartValidationError from './EquitySplitChartError';
import EquitySplitDonutChart from './EquitySplitDonutChart';
import { themeColors } from './common';
import { TDirectorIdToSharePercentageMap } from './types';

type EquitySplitInputProps = {
  directorVsSharePercentage: TDirectorIdToSharePercentageMap;
  setDirectorVsSharePercentage: React.Dispatch<
    React.SetStateAction<TDirectorIdToSharePercentageMap>
  >;
  readOnly?: boolean;
  equityInfo: TEquityInfoKS;
  onChangeShareholder: (val: string[]) => void;
  onEditShareholderDetails: (dirId: number) => void;
  directorIdsWithError?: number[];
  isDecimalEquityPercentageAllowed?: boolean;
  validationRule?: ValidationRule[];
  setNotification?: TSetNotification;
};

const EquityAmountInput = ({
  idx,
  max,
  name,
  onChange,
  value,
  isDecimalEquityPercentageAllowed = true,
  validationRule,
  director,
  setDirectorVsSharePercentage,
  directorVsSharePercentage,
  setNotification,
  readOnly = false,
}: {
  idx: number;
  max: number;
  name: string;
  onChange: (e: any) => void;
  value: number;
  readOnly?: boolean;
  isDecimalEquityPercentageAllowed?: boolean;
  validationRule?: ValidationRule[];
  director: any;
  setDirectorVsSharePercentage: React.Dispatch<
    React.SetStateAction<TDirectorIdToSharePercentageMap>
  >;
  directorVsSharePercentage: TDirectorIdToSharePercentageMap;
  setNotification?: TSetNotification;
}) => {
  const handleBlur = () => {
    if (validationRule && director[validationRule[0].qualifier]) {
      // Checking first rule's qualifier
      const validationResult = validateRules(value, validationRule);

      if (!validationResult.isValid) {
        const prevDirector = directorVsSharePercentage[director.id];

        setDirectorVsSharePercentage((prev) => ({
          ...prev,
          [director.id.toString()]: {
            ...prevDirector,
            sharePercentage: validationResult.adjustedValue,
          },
        }));

        setNotification({
          show: true,
          valid: false,
          title: 'Validation Error',
          message: validationResult.errors.join('. '),
        });
      }
    }
  };

  return (
    <div className="w-full flex items-center justify-between gap-4 grow shrink-0 pr-[80px]">
      <div className="flex items-center gap-2 w-full ">
        <span
          className="shrink-0 h-6 w-6 rounded flex items-center justify-center font-medium text-[10px]"
          style={{
            backgroundColor: `${themeColors[(idx + 1) % 6]}20`,
            color: themeColors[(idx + 1) % 6],
          }}
        >
          {getAvatarName(name)}
        </span>
        <span className="body-14-medium">{name}</span>
      </div>
      <div className="w-fit">
        {readOnly ? (
          <p className="txt-lg-medium text-text-primary flex gap-1">
            <b>{value}</b> %
          </p>
        ) : (
          <Input
            type="number"
            max={max}
            min={0}
            readOnly={readOnly}
            step={isDecimalEquityPercentageAllowed ? 0.01 : 1}
            className="w-20 hide-arrow"
            value={value}
            rightEl={() => <span className="translate-x-1">%</span>}
            onChange={onChange}
            onBlur={handleBlur}
            onWheel={(e) => e.currentTarget.blur()}
          />
        )}
      </div>
    </div>
  );
};

const EquitySplitInputKS = ({
  directorVsSharePercentage,
  setDirectorVsSharePercentage,
  equityInfo,
  onChangeShareholder,
  onEditShareholderDetails,
  directorIdsWithError,
  isDecimalEquityPercentageAllowed = true,
  validationRule,
  setNotification,
  readOnly,
}: EquitySplitInputProps) => {
  const getEquitySplitData = () => {
    let chartData = [];
    if (directorVsSharePercentage) {
      chartData = Object.values(directorVsSharePercentage).map((each) => ({
        name: each.name,
        sharePercentage: each.sharePercentage,
      }));
    }
    return chartData;
  };

  const totalPercentageEntered = Object.values(
    directorVsSharePercentage || {}
  ).reduce((each, acc) => each + (acc.sharePercentage || 0), 0);

  return (
    <div className="flex items-center gap-6">
      <div className="flex flex-col items-center gap-4">
        <div className="w-40 h-40 flex flex-col gap-6 items-center justify-center">
          {directorVsSharePercentage ? (
            <EquitySplitDonutChart
              owners={getEquitySplitData()}
              colors={themeColors}
              placeholderColor="#E5E5E5"
            />
          ) : null}
        </div>
        {directorVsSharePercentage ? (
          <ChartValidationError
            directorVsSharePercentage={directorVsSharePercentage}
            value={parseFloat(
              Object.values(directorVsSharePercentage || {})
                .reduce((each, acc) => each + (acc.sharePercentage || 0), 0)
                .toFixed(3)
            )}
          />
        ) : null}
      </div>
      <div className="flex flex-col w-full">
        <div className="">
          {Object.values(directorVsSharePercentage || {})
            .filter((dsp) => dsp.isShareholder)
            .map((director, idx) => {
              return (
                <div className="w-full border-b border-b-stroke-3 py-2.5 px-4">
                  <div className="flex gap-2 items-center justify-between relative">
                    <EquityAmountInput
                      setNotification={setNotification}
                      directorVsSharePercentage={directorVsSharePercentage}
                      validationRule={validationRule}
                      director={director}
                      setDirectorVsSharePercentage={
                        setDirectorVsSharePercentage
                      }
                      isDecimalEquityPercentageAllowed={
                        isDecimalEquityPercentageAllowed
                      }
                      value={
                        directorVsSharePercentage[director.id]?.sharePercentage
                      }
                      readOnly={readOnly}
                      name={director.name}
                      idx={idx}
                      key={director.id}
                      max={100}
                      onChange={(e) => {
                        const isValid =
                          e.target.value.length === 0 ||
                          (!Number.isNaN(parseFloat(e.target.value)) &&
                            parseFloat(e.target.value) > 0 &&
                            e.target.value.length <= 5 &&
                            (isDecimalEquityPercentageAllowed
                              ? true
                              : /^[0-9]+$/.test(e.target.value)));

                        if (!isValid) return;

                        const currentValue = e.target.value;

                        if (currentValue.includes('.')) {
                          const [intPart, decimalPart] =
                            currentValue.split('.');
                          const formattedIntPart =
                            intPart.replace(/^0+/, '') || '0';
                          e.target.value = `${formattedIntPart}.${decimalPart}`;
                        } else {
                          // For non-decimal numbers, remove leading zeros but allow a single zero
                          e.target.value =
                            currentValue.replace(/^0+/, '') || '0';
                        }

                        if (parseFloat(e.target.value) > 100) return;

                        setDirectorVsSharePercentage((prev) => ({
                          ...prev,
                          [director.id.toString()]: {
                            ...[director.id.toString()],
                            name: director.name,
                            sharePercentage: parseFloat(e.target.value),
                            isShareholder: true,
                            isBeneficialOwner: director.isBeneficialOwner,
                            id: director.id,
                            shareCount: parseInt(
                              (
                                (equityInfo.shareCount *
                                  parseFloat(e.target.value)) /
                                100
                              ).toString(),
                              10
                            ),
                          },
                        }));
                      }}
                    />

                    {!readOnly && (
                      <div className="absolute top-0 right-0 h-full flex items-center">
                        <div className="p-2 hover:bg-bg-secondary-hover rounded-sm">
                          <PencilLight
                            onClick={() =>
                              onEditShareholderDetails(director.id)
                            }
                            className="h-5 w-5 cursor-pointer text-button-tertiary-fg hover:text-button-tertiary-fg-hover "
                          />
                        </div>
                        <div className="p-2 hover:bg-bg-secondary-hover rounded-sm">
                          <XIcon
                            className="h-5 w-5 cursor-pointer text-button-tertiary-fg hover:text-button-tertiary-fg-hover "
                            onClick={() => {
                              onChangeShareholder(
                                Object.values(directorVsSharePercentage)
                                  ?.filter(
                                    (dsp) =>
                                      dsp.id !== director.id &&
                                      dsp.isShareholder
                                  )
                                  .map((dsp) => dsp.id?.toString())
                              );
                            }}
                          />
                        </div>
                      </div>
                    )}
                  </div>
                  {directorIdsWithError?.includes(director.id) ? (
                    <InputErrorMessage
                      className="pl-7"
                      message="Missing Details"
                    />
                  ) : null}
                </div>
              );
            })}
        </div>

        <div className="flex justify-between items-center pt-2.5 px-4 pr-[96px]">
          <span className="font-medium">Total</span>
          <div className="w-fit">
            {readOnly ? (
              <p className="txt-lg-medium text-text-primary">
                <b>{totalPercentageEntered}</b> %
              </p>
            ) : (
              <Input
                type="number"
                className="w-20 hide-arrow"
                value={totalPercentageEntered}
                rightEl={() => <span className="translate-x-1">%</span>}
                error={
                  totalPercentageEntered > 100
                    ? 'Total percentage cannot exceed 100%.'
                    : null
                }
                showErrorMessage={false}
                disabled
                readOnly={readOnly}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default EquitySplitInputKS;
